import { createContext } from 'react';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';

export type SupabaseConfig = {
  url: string;
  anonKey: string;
};

/**
 * Access status for the current user.
 * - 'unknown': Initial state, access hasn't been checked yet
 * - 'checking': Currently checking access status with backend
 * - 'granted': User has access (either no invite required or has valid invite)
 * - 'invite_required': User needs to enter an invite code to gain access
 * - 'error': Failed to check access status
 */
export type AccessStatus = 'unknown' | 'checking' | 'granted' | 'invite_required' | 'error';

export type SupabaseContextValue = {
  client: SupabaseClient | null;
  config: SupabaseConfig | null;
  session: Session | null;
  user: User | null;
  isReady: boolean;
  isSessionLoading: boolean;

  /**
   * Current access status for the user.
   * Use this to determine if the user can access protected content
   * or needs to enter an invite code.
   */
  accessStatus: AccessStatus;

  /**
   * Check access status with the backend.
   * Called automatically when session changes, but can be called manually
   * after applying an invite code.
   */
  checkAccess: () => Promise<void>;

  /**
   * Apply an invite code to grant access to the current user.
   * Throws an error if the code is invalid or already used.
   */
  applyInviteCode: (code: string) => Promise<void>;
};

export const SupabaseContext = createContext<SupabaseContextValue | null>(null);

